import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw } from 'lucide-react';
import { useVersion } from '../context/VersionContext';
import { VersionHistoryModal } from './VersionHistoryModal';
import { darfHinweisZeigen, HINWEIS_KEY } from '../lib/appVersion';

/**
 * Hinweis unten rechts, sobald eine neuere Version deployed wurde. Bewusst NICHT
 * wegklickbar: er soll bleiben, bis wirklich neu geladen wurde — sonst arbeitet
 * man weiter auf einem Bundle, dessen API-Gegenstück sich schon geändert hat.
 * Klick auf die Karte = Reload auf die neue Version.
 *
 * Gebündelt: An Tagen mit mehreren Deploys erscheint er höchstens alle 12 Stunden und
 * steht dann für alles seither — jede einzelne Änderung steht weiterhin im Verlauf
 * („Was ist neu?"). Der Zeitpunkt des letzten Hinweises liegt im localStorage, gilt
 * also je Gerät.
 */
export function UpdateToast() {
  const { info, running, updateAvailable } = useVersion();
  const [historyOpen, setHistoryOpen] = useState(false);
  // Einmal je Sichtbarwerden entschieden: sonst würde der Hinweis mitten in der Arbeit
  // verschwinden, sobald das 12-Stunden-Fenster durch den eigenen Merker zufällt.
  const [zeigen, setZeigen] = useState(false);

  useEffect(() => {
    if (!updateAvailable || zeigen) return;
    let zuletzt: string | null = null;
    try { zuletzt = localStorage.getItem(HINWEIS_KEY); } catch { /* z.B. privater Modus */ }
    if (!darfHinweisZeigen(Date.now(), zuletzt)) return;
    try { localStorage.setItem(HINWEIS_KEY, String(Date.now())); } catch { /* nicht schlimm */ }
    setZeigen(true);
  }, [updateAvailable, zeigen]);

  if (!updateAvailable || !zeigen) return null;

  return createPortal(
    <>
      <div className="fixed bottom-4 right-4 z-[105] max-w-[calc(100vw-2rem)] w-80">
        <div
          role="alert"
          onClick={() => window.location.reload()}
          className="cursor-pointer rounded-xl shadow-xl ring-1 ring-black/5 bg-blue-600 text-white p-4 animate-in fade-in slide-in-from-bottom-2"
        >
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Neue Version {info?.version} verfügbar</p>
              <p className="text-xs text-blue-100 mt-0.5">
                Sie arbeiten mit v{running}. Zum Aktualisieren hier klicken.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white text-blue-700">
                  <RefreshCw className="w-3.5 h-3.5" />
                  Jetzt aktualisieren
                </span>
                <button
                  onClick={e => { e.stopPropagation(); setHistoryOpen(true); }}
                  className="text-xs font-medium text-blue-100 underline hover:text-white"
                >
                  Was ist neu?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {historyOpen && <VersionHistoryModal onClose={() => setHistoryOpen(false)} />}
    </>,
    document.body,
  );
}
